import styles from 'css/src/components/app/SlideVideoPreview.module.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { TemplateStatusEnum } from 'js/src/client';
import { getSceneDesignStatus } from 'js/src/data/templates';
import type { RootState } from 'js/src/libs/RootState';
import { LuminaryContext } from 'js/src/luminary';
import { useLuminarySelector } from 'js/src/luminary/luminary-hooks';
import { LuminarySceneImage } from 'js/src/luminary/luminary-scene-image/luminary-scene-image';
import type { LuminarySceneImageRenderingObserver } from 'js/src/luminary/luminary-scene-image/luminary-scene-image-rendering-observer';

const ASPECT_RATIO_CLASS: Record<string, string> = {
    vertical: styles['vertical'],
    square: styles['square'],
    landscape: styles['landscape'],
    fourfive: styles['fourfive'],
};

type Props = {
    isActive?: boolean;
    onClick?: (e: React.MouseEvent, sId: string) => void;
    startSceneId: string;
    endSceneId: string;
    aspectRatio: string;
    luminaryRef: React.MutableRefObject<Luminary>;
    disableHoverPreview?: boolean;
    /** Keep playback running when the pointer leaves — for a caller that drives play itself. */
    keepPlayingOnMouseLeave?: boolean;
    renderingObserver?: LuminarySceneImageRenderingObserver;
    /** Offset (ms) from the start of the start scene at which to begin playback. */
    startSceneOffset?: number;
    /** Offset (ms) from the end of the end scene at which to stop playback. */
    endSceneOffset?: number;
    /**
     * Offset (ms) from the start scene's startTime at which to render the static poster. When set,
     * the poster shows the composited frame at that time (including the blend between the start and
     * end scenes if the time falls within a transition) instead of the start scene's own preview.
     */
    posterOffset?: number;
};

/**
 * Renders a preview of a specific scene in luminary given it's rendered canvasElement
 * The preview is muted and plays when hovered over
 */
const SlideVideoPreview = React.memo(function SlideVideoPreview({
    startSceneId,
    endSceneId,
    onClick,
    isActive,
    aspectRatio,
    luminaryRef,
    disableHoverPreview,
    keepPlayingOnMouseLeave,
    renderingObserver,
    startSceneOffset = 0,
    endSceneOffset = 0,
    posterOffset,
}: Props) {
    const STATUS_DNE = 'does_not_exist';
    const luminaryParentRef = React.useRef<HTMLDivElement | null>(null);

    const { playStartTime, playEndTime, sceneDesignId, posterTime } = useLuminarySelector(
        (luminary) => {
            const startScene = luminary.getSceneById(startSceneId);
            const endScene = luminary.getSceneById(endSceneId);

            return {
                playStartTime: startScene ? startScene.startTime + startSceneOffset : null,
                playEndTime: endScene ? endScene.endTime - endSceneOffset : null,
                sceneDesignId: startScene?.sceneDesign ?? null,
                posterTime:
                    posterOffset != null && startScene ? startScene.startTime + posterOffset : null,
            };
        },
        [startSceneId, endSceneId, startSceneOffset, endSceneOffset, posterOffset],
        { luminary: luminaryRef.current },
    );

    const sceneDesignStatus = useSelector((state: RootState) => {
        if (!sceneDesignId) return STATUS_DNE;
        return getSceneDesignStatus(state, sceneDesignId);
    });

    const isShowingPlaybackRef = React.useRef(false);

    const playCompleteCb = React.useCallback(() => {
        isShowingPlaybackRef.current = false;
        luminaryRef.current.stop();
        luminaryRef.current.canvasParent = null;
    }, [luminaryRef]);

    const clickCallback = React.useCallback(
        (e: React.MouseEvent) => {
            if (onClick) {
                onClick(e, startSceneId);
            }
        },
        [onClick, startSceneId],
    );

    React.useEffect(() => {
        const luminaryRefValue = luminaryRef.current;
        if (playStartTime === null || playEndTime === null) {
            return;
        }

        /**
         * Add playStart listener on change/mount.
         * Note: all SlideVideoPreview components add their own listener. Ex. in case there are 10 scenes,
         * 10 event listeners will be added to the luminary instance.
         * All 10 event listeners will be cleared on change/unmount by React.
         * Even though all event listeners will be triggered on playStart, only 1 callback (the matching one) wil run.
         */
        const playStartCb = (playStart: number, playEnd: number) => {
            const isThisPreview = playStart === playStartTime && playEnd === playEndTime;
            isShowingPlaybackRef.current = isThisPreview;
            if (isThisPreview) {
                luminaryRefValue.canvasParent = luminaryParentRef.current;
            }
        };
        const playCompleteListener = () => {
            if (!isShowingPlaybackRef.current) {
                return;
            }
            playCompleteCb();
        };
        luminaryRefValue.on('playStart', playStartCb);
        luminaryRefValue.on('playComplete', playCompleteListener);

        return () => {
            luminaryRefValue.off('playStart', playStartCb);
            luminaryRefValue.off('playComplete', playCompleteListener);
        };
    }, [luminaryRef, playStartTime, playEndTime, playCompleteCb]);

    const mouseEnter = () => {
        if (disableHoverPreview || playStartTime === null || playEndTime === null) return;

        luminaryRef.current.muted = true;
        luminaryRef.current.play({ startTime: playStartTime, endTime: playEndTime }).catch(() => {
            // another play started, ignore error
        });
    };

    const mouseLeave = () => {
        if (keepPlayingOnMouseLeave) return;
        playCompleteCb();
    };

    const showSpecialStatus =
        sceneDesignStatus !== TemplateStatusEnum.ACTIVE && sceneDesignStatus !== STATUS_DNE;

    return (
        <div
            className={classNames(styles['slide-video-preview'], ASPECT_RATIO_CLASS[aspectRatio], {
                [styles['active']]: isActive,
            })}
            onClick={clickCallback}
            onMouseEnter={mouseEnter}
            onMouseLeave={mouseLeave}
            ref={luminaryParentRef}
            data-testid="slide-video-preview"
        >
            <LuminaryContext value={luminaryRef.current}>
                <LuminarySceneImage
                    renderingObserver={renderingObserver}
                    sceneId={startSceneId}
                    renderingMode={VideoData.ALL_ASSETS_VISIBLE_SCENE_IMAGE_RENDER_MODE}
                    renderTime={posterTime ?? undefined}
                />
            </LuminaryContext>
            {showSpecialStatus && (
                <span className={`${styles['draft']} font-l5 font-l5--subtitle-l`}>
                    {sceneDesignStatus}
                </span>
            )}
        </div>
    );
});

export default SlideVideoPreview;
